import React from 'react';
import {
  CATALOG_CATEGORY_LABELS,
  type CatalogCategory,
  isLiCatalogCategory,
  isSdgCatalogCategory,
  normalizeSinceYouFollowLabel,
} from '../../constants/catalog-categories';

interface ProfileCategoryBadgeProps {
  category: CatalogCategory;
  /** Optional: override wrapper classes (default: positioned on profile edge, centered) */
  className?: string;
}

const getCategoryColor = (category: string): string => {
  // 1 = #376376 (Specially for you / Previously viewed)
  if (
    category === CATALOG_CATEGORY_LABELS.speciallyForYou ||
    category === CATALOG_CATEGORY_LABELS.previouslyViewed
  ) {
    return '#376376';
  }

  // 4 = #6c4273 (Women-led / Minority-led)
  if (
    category === CATALOG_CATEGORY_LABELS.womenLed ||
    category === CATALOG_CATEGORY_LABELS.pocOwned ||
    category === CATALOG_CATEGORY_LABELS.lgbtqOwned ||
    category === 'POC-led' ||
    category === 'LGBTQ+-led' ||
    category.endsWith('-led')
  ) {
    return '#6c4273';
  }

  // 5 = #34686a (Aligned with your selected SDGs / named SDG rows)
  if (isSdgCatalogCategory(category)) {
    return '#34686a';
  }

  // 6 = #638342 (Featured Impact Startup)
  if (category === CATALOG_CATEGORY_LABELS.featuredStartup) return '#638342';

  // 7 = #9c401a (Near you / local or geography based)
  if (
    category === CATALOG_CATEGORY_LABELS.europeBased ||
    category === CATALOG_CATEGORY_LABELS.usBased ||
    category === CATALOG_CATEGORY_LABELS.northAmericaBased ||
    category === CATALOG_CATEGORY_LABELS.latamBased ||
    category === CATALOG_CATEGORY_LABELS.nordicsBased ||
    category === CATALOG_CATEGORY_LABELS.africaBased ||
    category === CATALOG_CATEGORY_LABELS.nearYou ||
    category === CATALOG_CATEGORY_LABELS.otherIndustriesInYourLocation ||
    category === CATALOG_CATEGORY_LABELS.yourIndustriesInOtherLocations ||
    category.startsWith('Near you') ||
    category.startsWith('Startups from ') ||
    category.startsWith('More ') ||
    category.endsWith(' based') ||
    category.endsWith('-based') ||
    category.includes('-based') ||
    isLiCatalogCategory(category)
  ) {
    return '#9c401a';
  }

  // 2 = #6f6129 (Most Popular / Your Favorites / Based on your Favorites)
  if (
    category === CATALOG_CATEGORY_LABELS.yourFavorites ||
    category === 'Your favorites' ||
    category === CATALOG_CATEGORY_LABELS.basedOnYourFavorites ||
    category === CATALOG_CATEGORY_LABELS.mostPopular ||
    category.startsWith('Since you follow ') ||
    category.startsWith('Since you added ')
  ) {
    return '#6f6129';
  }

  // Recently added or default => black/dark
  return '#151515';
};

const ProfileCategoryBadge: React.FC<ProfileCategoryBadgeProps> = ({
  category,
  className = 'absolute -top-6 sm:-top-7 left-1/2 -translate-x-1/2 z-[200]',
}) => {
  const displayCategory = normalizeSinceYouFollowLabel(category);
  const bgColor = getCategoryColor(displayCategory);
  const isLongLabel = displayCategory.length > 36 || isLiCatalogCategory(displayCategory);

  return (
    <div className={className}>
      <span
        className={[
          'inline-block rounded-b-2xl border-b border-x border-gray-800 bg-clip-padding',
          'px-3 py-2 sm:px-5 sm:py-2.5',
          'text-center !font-normal normal-case tracking-normal text-white',
          'shadow-[0_8px_20px_rgba(0,0,0,0.22)]',
          // Long titles use a smaller size and stay between the corner icons
          'w-max whitespace-normal break-words',
          isLongLabel
            ? 'max-w-[calc(100%-5.5rem)] text-[11px] leading-tight'
            : 'max-w-full text-sm leading-snug sm:text-base',
        ].join(' ')}
        style={{ backgroundColor: bgColor, fontFamily: 'Rubik, sans-serif', fontWeight: 400 }}
      >
        {displayCategory}
      </span>
    </div>
  );
};

export default ProfileCategoryBadge;
